import React, { useState, useEffect } from "react";
import { useDocument } from "../../../../firebase-files/hooks/useDocument";
import { useAuthContext } from "../../../../firebase-files/hooks/useAuthContext";
import { useFirestore } from "../../../../firebase-files/hooks/useFirestore";
import { projectStorage } from "../../../../firebase-config/config";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { timestamp } from "../../../../firebase-config/config";
import { projectFirestore } from "../../../../firebase-config/config";
import { recognizeFace } from "../../../../faceRecognition/models/faceRecognition.module";
import "./PGSecurityOneVisitorLogs.scss";
import PGLoaders from "../../../../default-pages/PGLoaders/PGLoaders";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";

const SecurityOneVisitorLogs = () => {
  const { user } = useAuthContext();
  const { id } = useParams();
  const navigate = useNavigate();

  // Fetch the visitor document from Firestore
  const { document: userDocs } = useDocument("users", id);

  const { updateDocument } = useFirestore("visitors");

  const [visitorDetails, setVisitorDetails] = useState({
    visitorName: "",
    visitorEmail: "",
  });
  const [status, setStatus] = useState("");
  const [faceRegistered, setFaceRegistered] = useState(false);
  const [updating, isUpdating] = useState(false);
  const [totalVisitors, setTotalVisitors] = useState(1);

  const [createdByUser, setCreatedByUser] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(
    localStorage.getItem("unmatchedFace")
  );

  const { addDocument: addVisitorDoc } = useFirestore("visitors");

  const handleSliderChange = (event, newValue) => {
    setTotalVisitors(newValue);
  };
  function valuetext(value) {
    return `${value}°C`;
  }

  useEffect(() => {
    const fetchVisitorAndUserDetails = async () => {
      try {
        let latestVisitorData = {};

        const visitorQuerySnapshot = await projectFirestore
          .collection("visitors")
          .where("phoneNumber", "==", id)
          .orderBy("createdAt", "desc")
          .limit(1)
          .get();

        let hasExited = true;

        if (!visitorQuerySnapshot.empty) {
          const latestDoc = visitorQuerySnapshot.docs[0];
          latestVisitorData = latestDoc.data();

          if (!latestVisitorData.visitorExitTimeStamp) {
            hasExited = false;
          }
        }

        if (hasExited) {
          // Visitor exited, so show empty fields except basic visitor info
          setVisitorDetails({
            visitorName: userDocs?.fullName || "",
            visitorEmail: userDocs?.email || "",
            phoneNumber: userDocs?.phoneNumber || id,
            purpose: "",
            remarks: "",
            hostName: "",
            hostEmail: "",
            hostMobileNumber: "",
            createdBy: "",
            createdAt: "",
          });
        } else {
          // Ongoing visit, show existing details
          setVisitorDetails({
            visitorName: userDocs?.fullName || "",
            visitorEmail: userDocs?.email || "",
            phoneNumber: userDocs?.phoneNumber || id,
            purpose: latestVisitorData?.purpose || "",
            remarks: latestVisitorData?.remarks || "",
            hostName: latestVisitorData?.hostName || "",
            hostEmail: latestVisitorData?.hostEmail || "",
            visitorExitTimeStamp: latestVisitorData?.visitorExitTimeStamp || "",
            hostMobileNumber: latestVisitorData?.hostMobileNumber || "",
            createdBy: latestVisitorData?.createdBy || "",
            createdAt: latestVisitorData?.createdAt || "",
          });
        }
      } catch (error) {
        console.error("Error fetching visitor details:", error);
      }
    };

    if (userDocs) {
      fetchVisitorAndUserDetails();
    }
  }, [userDocs, id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setVisitorDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  useEffect(() => {
    const fetchCreatedByUser = async () => {
      try {
        const visitorQuerySnapshot = await projectFirestore
          .collection("visitors")
          .where("phoneNumber", "==", id)
          .orderBy("createdAt", "desc")
          .limit(1)
          .get();

        if (!visitorQuerySnapshot.empty) {
          const latestDoc = visitorQuerySnapshot.docs[0];
          const latestData = latestDoc.data();
          const createdBy = latestData.createdBy;

          if (createdBy) {
            const userDoc = await projectFirestore
              .collection("users")
              .doc(createdBy)
              .get();
            if (userDoc.exists) {
              setCreatedByUser(userDoc.data());
            }
          }
        }
      } catch (error) {
        console.error("Error fetching createdBy user:", error);
      }
    };

    fetchCreatedByUser();
  }, [id]);

  const handleConfirmClick = async () => {
    try {
      isUpdating(true);
      const currentTimestamp = timestamp.fromDate(new Date());
      let updatedData = { ...visitorDetails };

      let faceImageUrl = "";
      let faceDescriptor = null;
      let faceDescriptorEntry = null;
      console.log("previewUrl:", previewUrl);

      if (id === "new") {
        // 👤 Face registration logic starts
        if (previewUrl) {
          const img = new Image();
          img.src = previewUrl;

          await new Promise((resolve, reject) => {
            img.onload = async () => {
              try {
                // Recognize face
                faceDescriptor = await recognizeFace(img);

                // Upload new image
                const blob = await (await fetch(previewUrl)).blob();
                const imageName = `face_${Date.now()}.png`;
                const uploadPath = `registered-faces-for-scan/${updatedData.phoneNumber}/${imageName}`;
                const storageRef = projectStorage.ref(uploadPath);
                console.log("face uploading...");

                await storageRef.put(blob);
                faceImageUrl = await storageRef.getDownloadURL();
                console.log("uploaded img url:", faceImageUrl);

                faceDescriptorEntry = {
                  faceDescriptor: Array.from(faceDescriptor),
                  createdAt: timestamp.now(),
                };
                localStorage.removeItem("unmatchedFace");

                setStatus("✅ Face registered!");
                setFaceRegistered(true);
                resolve();
              } catch (error) {
                console.error("Face registration error:", error);
                setStatus("❌ Error saving face");
                reject(error);
              }
            };

            img.onerror = () => {
              setStatus("❌ Failed to load face image.");
              reject(new Error("Image load failed"));
            };
          });
        }
        // Create new user document for first-time visitor
        await projectFirestore
          .collection("users")
          .doc(visitorDetails.phoneNumber)
          .set({
            uid: "",
            role: "customer",
            roles: ["customer"],
            online: true,
            displayName: "",
            fullName: updatedData.visitorName,
            gender: "",
            phoneNumber: updatedData.phoneNumber,
            email: updatedData.visitorEmail,
            city: "",
            country: "",
            countryCode: "",
            address: "",
            addresses: "",
            photoURL: "",
            status: "active",
            isAttendance: false,
            vehicleNumberPlate: "",
            vehicleStatus: "",
            vehicleType: "",
            isStaff: "",
            drivingLicence: "",
            accessType: "",
            accessValue: "",
            faceRegistration: faceDescriptorEntry ? [faceDescriptorEntry] : [],
            registeredFaceUrl: faceImageUrl || "",
            createdAt: "",
          });

        const newVisitor = {
          ...updatedData,
          visitorEntryTimeStamp: currentTimestamp,
          visitorExitTimeStamp: "",
          totalVisitors: totalVisitors,
          createdBy: user?.phoneNumber,
          createdAt: new Date(),
          isPreApprove: false,
        };
        await addVisitorDoc(newVisitor);
        navigate("/host-home");
        console.log("New visitor and user added successfully!");
      } else {
        // Update or insert based on existing visitor record
        const visitorQuerySnapshot = await projectFirestore
          .collection("visitors")
          .where("phoneNumber", "==", id)
          .orderBy("createdAt", "desc")
          .limit(1)
          .get();

        if (!visitorQuerySnapshot.empty) {
          const latestDoc = visitorQuerySnapshot.docs[0];
          const latestData = latestDoc.data();
          const latestDocId = latestDoc.id;

          if (
            latestData.visitorEntryTimeStamp &&
            !latestData.visitorExitTimeStamp
          ) {
            // User is exiting
            await updateDocument(latestDocId, {
              visitorExitTimeStamp: currentTimestamp,
              updatedBy: user?.phoneNumber,
            });
            console.log("Exit time updated.");

            // Update face info in user doc
            if (faceDescriptorEntry) {
              await updateDocument(updatedData.phoneNumber, {
                faceRegistration: [faceDescriptorEntry],
                registeredFaceUrl: faceImageUrl,
              });
            }
            navigate("/host-home");
            console.log("Visitor exit updated with face info (if any).");
          } else {
            // No ongoing visit; create a new visitor record
            const newVisitor = {
              ...updatedData,
              visitorEntryTimeStamp: currentTimestamp,
              visitorExitTimeStamp: "",
              totalVisitors: totalVisitors,
              createdBy: user?.phoneNumber,
              createdAt: new Date(),
              isPreApprove: false,
            };
            await addVisitorDoc(newVisitor);
            navigate("/host-home");
            console.log("New visitor entry created.");
          }
        } else {
          // No previous visits found; create new
          const newVisitor = {
            ...updatedData,
            visitorEntryTimeStamp: currentTimestamp,
            visitorExitTimeStamp: "",
            totalVisitors: totalVisitors,
            createdBy: user?.phoneNumber,
            createdAt: new Date(),
            isPreApprove: false,
          };
          await addVisitorDoc(newVisitor);
          navigate("/host-home");
          console.log("First-time visitor entry created.");
        }
      }
    } catch (error) {
      console.error("Error updating visitor details:", error);
    } finally {
      isUpdating(false);
    }
  };

  return (
    <div className="security-one-visitor-details-parent container">
      <div className="d-flex justify-content-center security-one-visitor-details-div">
        <img
          src={
            userDocs?.registeredFaceUrl || localStorage.getItem("unmatchedFace")
          }
          alt="your registred face"
          className="security-one-visitor-details-img"
        />
      </div>
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ marginTop: "30px" }}
      >
        {userDocs && (
          <>
            {userDocs.gender === "male" && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="20px"
                viewBox="0 -960 960 960"
                width="20px"
                fill="currentColor"
              >
                <path d="M400-80v-280h-80v-240q0-33 23.5-56.5T400-680h160q33 0 56.5 23.5T640-600v240h-80v280H400Zm80-640q-33 0-56.5-23.5T400-800q0-33 23.5-56.5T480-880q33 0 56.5 23.5T560-800q0 33-23.5 56.5T480-720Z" />
              </svg>
            )}
            {userDocs.gender === "female" && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="20px"
                viewBox="0 -960 960 960"
                width="20px"
                fill="currentColor"
              >
                <path d="M400-80v-240H280l122-308q10-24 31-38t47-14q26 0 47 14t31 38l122 308H560v240H400Zm80-640q-33 0-56.5-23.5T400-800q0-33 23.5-56.5T480-880q33 0 56.5 23.5T560-800q0 33-23.5 56.5T480-720Z" />
              </svg>
            )}
          </>
        )}

        <p style={{ textTransform: "capitalize" }}>
          {userDocs && userDocs.gender}
        </p>
      </div>

      {/* peoples group counter start */}
      <div className="security-one-visitor-peoples-parent">
        <div
          className="d-flex justify-content-between align-items-center"
          style={{ fontWeight: "700" }}
        >
          <div
            className="security-one-visitor-peoples-decrease"
            onClick={() => setTotalVisitors((prev) => Math.max(prev - 1, 1))}
          >
            -
          </div>
          Total Visitors: {totalVisitors}
          <div
            className="security-one-visitor-peoples-decrease"
            onClick={() => setTotalVisitors((prev) => prev + 1)}
          >
            +
          </div>
        </div>
        <Box sx={{ width: 300 }}>
          <Slider
            aria-label="Temperature"
            // defaultValue={1}
            getAriaValueText={valuetext}
            value={totalVisitors}
            onChange={handleSliderChange}
            valueLabelDisplay="auto"
            // shiftStep={30}
            // step={1}
            marks
            min={1}
            max={15}
          />
        </Box>
      </div>
      {/* peoples group counter end */}

      {/* VISITOR DETAILS */}
      <div className="security-one-visitor-details-body">
        <span className="security-one-strip">VISITOR DETAILS</span>
        <input
          type="text"
          name="visitorName"
          value={userDocs && userDocs.fullName}
          onChange={handleInputChange}
          placeholder="Full Name"
        />
        <input
          type="text"
          name="phoneNumber"
          value={userDocs && userDocs.phoneNumber}
          onChange={handleInputChange}
          placeholder="Phone Number"
        />
        <input
          type="email"
          name="visitorEmail"
          value={userDocs && userDocs.email}
          onChange={handleInputChange}
          placeholder="Email"
        />
      </div>

      {/* PURPOSE OF VISIT */}
      <div className="security-one-visitor-details-body">
        <span className="security-one-strip">PURPOSE OF VISIT</span>
        <input
          type="text"
          name="purpose"
          value={visitorDetails.purpose}
          onChange={handleInputChange}
          placeholder="Purpose"
        />
        <input
          type="text"
          name="remarks"
          value={visitorDetails.remarks}
          onChange={handleInputChange}
          placeholder="Remark"
        />
      </div>

      {/* HOST DETAILS */}
      <div className="security-one-visitor-details-body">
        <span className="security-one-strip">HOST DETAILS</span>
        <input
          type="text"
          name="hostName"
          value={visitorDetails.hostName}
          onChange={handleInputChange}
          placeholder="Full Name"
        />
        <input
          type="text"
          name="hostMobileNumber"
          value={visitorDetails.hostMobileNumber}
          onChange={handleInputChange}
          placeholder="Phone Number"
        />
        <input
          type="email"
          name="hostEmail"
          value={visitorDetails.hostEmail}
          onChange={handleInputChange}
          placeholder="Email"
        />
      </div>

      {/* CREATED BY */}
      {visitorDetails.visitorExitTimeStamp === "" && (
        <div className="security-one-visitor-details-body">
          <span className="security-one-strip">CREATED BY</span>
          <input
            type="text"
            name="name"
            value={createdByUser && createdByUser.fullName}
            disabled
          />
          <input
            type="text"
            name="createdBy"
            value={visitorDetails.createdBy}
            disabled
          />
          <input
            type="text"
            name="createdAt"
            value={
              visitorDetails.createdAt &&
              visitorDetails.createdAt
                .toDate()
                .toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                })
                .replace("", "")
                .replace(",", ", ")
            }
            disabled
          />
        </div>
      )}

      {updating ? (
        <PGLoaders />
      ) : (
        <div className="d-flex justify-content-end">
          <button className="btn_fill" onClick={handleConfirmClick}>
            {visitorDetails.visitorExitTimeStamp === "" ? "Exit" : "Enter"}
          </button>
        </div>
      )}
    </div>
  );
};

export default SecurityOneVisitorLogs;
